import React from 'react';
import { createFilter } from 'react-select';
import Select from '../../../../components/Select';
import { usePopularCodeLanguages } from '../../../../ui/atoms';
import { PlainLanguage, languageList } from '../../../code/Prism';
import ToolbarButton from './ToolbarButton';
import * as styles from './ToolbarSyntaxButton.module.scss';

interface ToolbarSyntaxButtonProps {
  languageKey?: string | null;
  onDropdownOpenChange: (open: boolean) => void;
  onSelect: (key: string) => void;
}

const ToolbarSyntaxButton = ({
  languageKey = PlainLanguage.key,
  onDropdownOpenChange,
  onSelect,
}: ToolbarSyntaxButtonProps) => {
  const currentLanguage =
    languageKey && languageList.find(({ key }) => key === languageKey);

  const popularCodeLanguages = usePopularCodeLanguages();

  const topPopularOptions = Object.entries(popularCodeLanguages)
    .toSorted(([, a], [, b]) => b - a)
    .slice(0, 5)
    .flatMap((item) => {
      const language = languageList.find(({ key }) => key === item[0]);
      return language
        ? [
            {
              label: language.label,
              value: language.key,
              aliases: language.aliases,
            },
          ]
        : [];
    })
    .toSorted((a, b) => a.label.localeCompare(b.label));

  const allOptions = languageList.map(({ key, label, aliases }) => ({
    label,
    value: key,
    aliases,
  }));

  return (
    <ToolbarButton
      rightAlignPopOver
      renderPopOver={(onClose) => (
        <div className={styles.container}>
          <Select
            autoFocus
            menuIsOpen
            disableMenuPortal
            components={{
              DropdownIndicator: null,
            }}
            controlShouldRenderValue={false}
            hideSelectedOptions={false}
            isClearable={false}
            backspaceRemovesValue={false}
            value={languageKey}
            onChange={(key) => {
              onSelect(key);
              onClose();
            }}
            placeholder="Search for languages..."
            menuPlacement="bottom"
            options={[
              { label: 'Popular', options: topPopularOptions },
              { label: 'All', options: allOptions },
            ]}
            filterOption={createFilter({
              stringify: ({ label, value, data }) =>
                `${label} ${value} ${data.aliases?.join(' ') ?? ''}`,
            })}
          />
        </div>
      )}
      onPopOverOpenChange={onDropdownOpenChange}
    >
      {currentLanguage ? currentLanguage.label : PlainLanguage.label}
    </ToolbarButton>
  );
};

export default ToolbarSyntaxButton;
